import { ROOT_DECISION_NODE_ID } from '@vms/vmspro3-core/dist/systemConsts'
import { splitAncestry } from '@vms/vmspro3-core/dist/utils/ancestry'
import { DecisionFolder } from '@vms/vmspro3-core/src/types'
import { TreeSelect } from 'antd'
import { DefaultOptionType } from 'antd/lib/select'
import { gql } from 'graphql-tag'
import { useMemo, useState } from 'react'
import { useAccount } from '../../context'
import Server from '../../server/VMSProServerAdapter'
import { keyBy } from 'lodash'

const DECISION_ANCESTRY_FOLDERS_WITH_CHILDREN = gql`
  query AllFolders($accountId: ID!) {
    allFolders(accountId: $accountId) {
      id
      ancestry
      name
    }
  }
`

function foldersToTreeData(folders: DecisionFolder[], rootFolderName = 'Decisions') {
  const treeData = [
    {
      id: ROOT_DECISION_NODE_ID,
      pId: undefined,
      value: ROOT_DECISION_NODE_ID,
      title: rootFolderName,
      isLeaf: true, // default to true; we'll correct in the next step
      isExpanded: true,
    },
    ...folders.map(folder => {
      const pId = splitAncestry(folder.ancestry).pop()
      return {
        id: folder.id,
        pId,
        value: folder.id,
        title: folder.name,
        isLeaf: true, // default to true; we'll correct in the next step
      }
    }),
  ]
  const parentIds = new Set(treeData.map(d => d.pId))
  return treeData.map(d => {
    if (parentIds.has(d.id)) d.isLeaf = false
    return d
  })
}

interface DecisionFolderTreeSelectProps {
  initialExpandedFolderId: string
  onSelect: (folderId: string) => void
}
export function DecisionFolderTreeSelect(props: DecisionFolderTreeSelectProps) {
  const {
    accountId,
    account: { name: accountName },
  } = useAccount()

  const [data, setData] = useState<{
    folders: Omit<DefaultOptionType, 'label'>[]
    initialExpandedIds: string[]
  }>({
    folders: [],
    initialExpandedIds: [],
  })

  const { folders, initialExpandedIds } = data
  const loading = folders.length === 0
  const { initialExpandedFolderId, onSelect } = props

  useMemo(() => {
    Server.graphql({
      query: DECISION_ANCESTRY_FOLDERS_WITH_CHILDREN,
      variables: { accountId, folderId: initialExpandedFolderId },
    }).then(resp => {
      const { allFolders } = resp.data
      const foldersById: Record<string, { id: string; ancestry: string; name: string }> = {
        ...keyBy(allFolders, 'id'),
        [ROOT_DECISION_NODE_ID]: { id: ROOT_DECISION_NODE_ID, ancestry: '/', name: '' },
      }
      const folders = foldersToTreeData(allFolders)
      const initialExpandedFolder = foldersById[initialExpandedFolderId]
      const expandedFolders =
        initialExpandedFolder &&
        [...splitAncestry(initialExpandedFolder.ancestry), initialExpandedFolderId].map(id => foldersById[id])
      const initialExpandedIds = expandedFolders
        ? expandedFolders.map(f => f.id).filter(Boolean)
        : [ROOT_DECISION_NODE_ID]
      setData({ folders, initialExpandedIds })
    })
  }, [accountId, initialExpandedFolderId])

  return (
    <TreeSelect
      style={{ width: '100%' }}
      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
      placeholder={loading ? 'Loading folders, please wait...' : 'Select destination folder'}
      treeDataSimpleMode
      treeData={folders}
      treeDefaultExpandedKeys={initialExpandedIds}
      onSelect={onSelect}
    />
  )
}
